import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-conditions-page">
      {/* Header Banner */}
      <div className="bg-slate-900 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-slate-400 hover:text-white transition-colors mb-6"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-[#0047AB] rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Terms & Conditions
            </h1>
          </div>
          <p className="text-slate-400">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="legal-page prose prose-slate max-w-none">
          
          <section>
            <h2>1. Business Identity and Services</h2>
            <p>
              These Terms and Conditions ("Terms") govern your use of the SMS text messaging 
              services provided by <strong>Muscle & Movement Rehab</strong>, a healthcare clinic 
              located at 5800 Sheppard Avenue East, Unit 13, Scarborough, ON M1B 5J7.
            </p>
            <p>We offer the following services:</p>
            <ul>
              <li>Physiotherapy</li>
              <li>Chiropractic Care</li>
              <li>Registered Massage Therapy</li>
              <li>Naturopathic Care</li>
              <li>Fitness Programs</li>
            </ul>
            <p>
              By opting in to receive SMS text messages from us, you agree to these Terms and 
              our <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>2. Types of Messages</h2>
            <p>
              When you opt in to our SMS messaging service, you may receive the following types 
              of messages:
            </p>
            
            <h3>Non-Marketing Messages (Service-Related)</h3>
            <ul>
              <li>Appointment reminders and confirmations</li>
              <li>Booking follow-ups and scheduling changes</li>
              <li>Service-related notifications</li>
              <li>Account and billing updates</li>
              <li>Responses to your inquiries</li>
            </ul>

            <h3>Marketing Messages (Promotional)</h3>
            <p>
              Only if you explicitly consent, you may also receive:
            </p>
            <ul>
              <li>Special offers and discounts</li>
              <li>Information about new services</li>
              <li>Promotional content and updates</li>
            </ul>
            <p>
              <strong>Important:</strong> Marketing messages require separate consent and are 
              completely optional.
            </p>
          </section>

          <section>
            <h2>3. Message Frequency</h2>
            <p>
              <strong>Message frequency may vary</strong> depending on your appointment schedule 
              and the type of communications you have opted into. Typically, you can expect:
            </p>
            <ul>
              <li>1-3 appointment-related messages per scheduled visit</li>
              <li>Occasional service updates as needed</li>
              <li>Marketing messages (if opted in): No more than 4 per month</li>
            </ul>
          </section>

          <section>
            <h2>4. Message and Data Rates</h2>
            <p>
              <strong>Message and data rates may apply.</strong> Standard messaging rates from 
              your mobile carrier apply to all SMS messages sent to and from our service. 
              Contact your wireless provider for details about your messaging plan.
            </p>
            <p>
              As always, message and data rates may apply for any messages sent to you from us 
              and to us from you. If you have any questions about your text plan or data plan, 
              it is best to contact your wireless provider.
            </p>
          </section>

          <section className="bg-amber-50 p-6 rounded-lg border border-amber-200 my-8">
            <h2 className="text-amber-800 mt-0">5. How to Opt Out (STOP)</h2>
            <p>
              You can cancel the SMS service at any time. Just text <strong>"STOP"</strong> to 
              <strong> +1 647-490-3782</strong>. After you send the SMS message "STOP" to us, we 
              will send you an SMS message to confirm that you have been unsubscribed. After this, 
              you will no longer receive SMS messages from us.
            </p>
            <p>
              If you want to join again, just sign up as you did the first time and we will 
              start sending SMS messages to you again.
            </p>
            <p className="mb-0">
              If you are experiencing issues with the messaging program you can reply with the 
              keyword <strong>HELP</strong> for more assistance, or you can get help directly at{" "}
              <a href="mailto:admin@muscleandmovement.com">admin@muscleandmovement.com</a> or{" "}
              <a href="tel:+16474903782">+1 647-490-3782</a>.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg border border-blue-100 my-8">
            <h2 className="text-[#0047AB] mt-0">6. How to Get Help (HELP)</h2>
            <p>
              If you are experiencing issues with the messaging program, you can reply with 
              the keyword <strong>"HELP"</strong> for more assistance.
            </p>
            <p className="mb-0">
              You can also get help directly by:
            </p>
            <ul className="mb-0">
              <li>Email: <a href="mailto:admin@muscleandmovement.com">admin@muscleandmovement.com</a></li>
              <li>Phone: <a href="tel:+16474903782">+1 647-490-3782</a></li>
            </ul>
          </section>

          <section>
            <h2>7. Carrier Liability Disclaimer</h2>
            <p>
              <strong>Carriers are not liable for delayed or undelivered messages.</strong> Message 
              delivery is subject to effective transmission from your network operator. Muscle & 
              Movement Rehab is not responsible for messages that are delayed or not delivered 
              due to:
            </p>
            <ul>
              <li>Network congestion or technical issues</li>
              <li>Carrier service interruptions</li>
              <li>Device incompatibility</li>
              <li>Incorrect phone number provided</li>
              <li>Other factors outside our control</li>
            </ul>
          </section>

          <section>
            <h2>8. Consent and Authorization</h2>
            <p>
              <strong>You must be 18 years of age or older to opt in to our SMS messaging service.</strong>
            </p>
            <p>
              By providing your mobile phone number and checking the consent boxes on our form, 
              you expressly consent to receive automated text messages from Muscle & Movement 
              Rehab at the phone number provided.
            </p>
            <p>
              Consent is not required as a condition of purchasing any goods or services. You 
              may revoke your consent at any time by texting STOP.
            </p>
          </section>

          <section>
            <h2>9. Privacy and Data Protection</h2>
            <p>
              Your personal information, including your phone number and consent preferences, 
              is protected in accordance with our <Link to="/privacy-policy">Privacy Policy</Link>. 
              We do not sell, rent, or share your mobile information with third parties for 
              marketing purposes.
            </p>
            <p>
              If you have any questions regarding privacy, please read our{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>10. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective 
              immediately upon posting to our website. Your continued participation in our SMS 
              program after any changes constitutes acceptance of the updated Terms.
            </p>
            <p>
              We encourage you to review these Terms periodically for any updates.
            </p>
          </section>

          <section>
            <h2>11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the 
              Province of Ontario and the federal laws of Canada applicable therein, without 
              regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2>12. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the 
              remaining provisions shall continue in full force and effect.
            </p>
          </section>

          <section>
            <h2>13. Contact Information</h2>
            <p>
              For questions about these Terms or our SMS messaging program, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg mt-4">
              <p className="font-semibold text-slate-800 mb-2">Muscle & Movement Rehab</p>
              <p className="mb-1">5800 Sheppard Avenue East, Unit 13</p>
              <p className="mb-1">Scarborough, ON M1B 5J7</p>
              <p className="mb-1">
                Email: <a href="mailto:admin@muscleandmovement.com">admin@muscleandmovement.com</a>
              </p>
              <p>
                Phone: <a href="tel:+16474903782">+1 647-490-3782</a>
              </p>
            </div>
          </section>

          {/* Summary Box */}
          <section className="bg-slate-100 p-6 rounded-lg mt-8">
            <h2 className="mt-0">Quick Reference Summary</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-slate-800 mb-2">To Opt Out:</p>
                <p className="text-slate-600 mb-0">Text <strong>STOP</strong> to +1 647-490-3782</p>
              </div>
              <div>
                <p className="font-semibold text-slate-800 mb-2">For Help:</p>
                <p className="text-slate-600 mb-0">Text <strong>HELP</strong> or call +1 647-490-3782</p>
              </div>
            </div>
          </section>

        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="font-heading font-semibold text-slate-800 mb-4">Related Documents</h3>
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/privacy-policy" 
              className="text-[#0047AB] hover:underline"
              data-testid="related-privacy-link"
            >
              Privacy Policy
            </Link>
            <a 
              href="https://muscleandmovement.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#0047AB] hover:underline"
            >
              Main Website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
